import type { ComponentType } from 'react'
import {
  Archive,
  BarChart3,
  BriefcaseBusiness,
  Building2,
  CalendarCheck,
  Camera,
  CheckCircle2,
  CheckSquare2,
  ClipboardList,
  Clock3,
  FileText,
  Headphones,
  HeartHandshake,
  Laptop,
  Megaphone,
  MonitorCog,
  PackageCheck,
  PhoneCall,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react'
import type { AccessUser } from '@/types/api'
import { isCeo } from './access-control'

export interface RoleMetric {
  label: string
  value: string
  detail: string
  icon: ComponentType<{ className?: string }>
}

export interface RoleWorkItem {
  title: string
  meta: string
  status: string
}

export interface RolePanel {
  title: string
  description: string
  items: RoleWorkItem[]
}

export interface RoleQuickAction {
  label: string
  href: string
  icon: ComponentType<{ className?: string }>
}

export interface RoleExperience {
  key: string
  roleCodes: string[]
  workspaceTitle: string
  sidebarLabel: string
  splashTitle: string
  splashMessage: string
  dashboardTitle: string
  dashboardDescription: string
  focusLabel: string
  focusTitle: string
  focusDescription: string
  metrics: RoleMetric[]
  primaryPanel: RolePanel
  secondaryPanel: RolePanel
  quickActions: RoleQuickAction[]
}

export const roleExperiences: RoleExperience[] = [
  {
    key: 'ceo',
    roleCodes: ['CEO'],
    workspaceTitle: 'Chief Executive Officer Workspace',
    sidebarLabel: 'Executive leadership',
    splashTitle: 'Welcome back, Chief Executive Officer.',
    splashMessage: 'Your full company view, leadership decisions, and workspace controls are loading.',
    dashboardTitle: 'Chief Executive Officer Dashboard',
    dashboardDescription: 'Full workspace oversight of people, delivery, revenue, confidential records, and company-wide decisions.',
    focusLabel: 'Executive leadership',
    focusTitle: 'Company-wide visibility and decisions',
    focusDescription: 'Review the whole business, unblock your teams, and manage leadership access from one workspace.',
    metrics: [],
    primaryPanel: {
      title: 'Executive decisions',
      description: 'Company-wide approvals and priorities.',
      items: [],
    },
    secondaryPanel: {
      title: 'Company oversight',
      description: 'People, operations, and financial performance.',
      items: [],
    },
    quickActions: [
      { label: 'Manage leadership access', href: '/roles', icon: ShieldCheck },
      { label: 'Review staff delivery', href: '/tasks', icon: ClipboardList },
      { label: 'Company reports', href: '/reports', icon: BarChart3 },
      { label: 'Review invoices', href: '/invoices', icon: FileText },
    ],
  },
  {
    key: 'general-manager',
    roleCodes: ['GENERAL_MANAGER'],
    workspaceTitle: 'General Manager Command Center',
    sidebarLabel: 'Executive operations',
    splashTitle: 'Welcome back, General Manager.',
    splashMessage: 'Today’s staff attendance, approvals, delivery risks, and programme movement are coming into focus.',
    dashboardTitle: 'General Manager Command Center',
    dashboardDescription: 'Executive command view of who is in today, staff delivery, approvals, operational risk, and programme momentum.',
    focusLabel: 'Today needs your decision',
    focusTitle: 'People presence, approvals, blockers, and leadership visibility',
    focusDescription: 'Start with who is in today, then the queues that can slow the company down if management does not act.',
    metrics: [
      { label: 'Pending approvals', value: '7', detail: 'Leave, procurement, and reports', icon: ShieldCheck },
      { label: 'Overdue work', value: '3', detail: 'Requires escalation today', icon: Clock3 },
      { label: 'Active programmes', value: '4', detail: '2 cohorts opening soon', icon: BriefcaseBusiness },
      { label: 'Sensitive alerts', value: '1', detail: 'EAP case needs follow-up', icon: HeartHandshake },
    ],
    primaryPanel: {
      title: 'Management queue',
      description: 'Items waiting for executive review.',
      items: [
        { title: 'Review HR leave exceptions', meta: 'People Operations - 3 pending', status: 'PENDING' },
        { title: 'Approve projector procurement', meta: 'Administration - NGN 300,000 estimate', status: 'SUBMITTED' },
        { title: 'Check overdue client follow-ups', meta: 'CRM - 2 stalled relationships', status: 'URGENT' },
      ],
    },
    secondaryPanel: {
      title: 'Leadership pulse',
      description: 'Signals from teams and programmes.',
      items: [
        { title: 'Weekly task plans visible', meta: '6 of 9 staff submitted', status: 'PARTIAL' },
        { title: 'Next programme cohort', meta: 'July 2026 - 18 prospects', status: 'OPEN' },
        { title: 'EAP utilization review', meta: '1 active case with sensitive access', status: 'ACTIVE' },
      ],
    },
    quickActions: [
      { label: 'Today attendance', href: '/attendance', icon: Clock3 },
      { label: 'Review weekly tasks', href: '/tasks', icon: ClipboardList },
      { label: 'Review leave', href: '/leave', icon: CalendarCheck },
      { label: 'Export reports', href: '/reports', icon: BarChart3 },
    ],
  },
  {
    key: 'hr-admin',
    roleCodes: ['HR_ADMIN'],
    workspaceTitle: 'HR & Admin Console',
    sidebarLabel: 'People operations',
    splashTitle: 'Good to see you, HR.',
    splashMessage: 'Attendance, leave, staff records, and admin requests are loading.',
    dashboardTitle: 'HR & Admin Console',
    dashboardDescription: 'People operations workspace for staff records, leave decisions, attendance, assets, and admin requests.',
    focusLabel: 'People operations priority',
    focusTitle: 'Keep staff records and approvals moving',
    focusDescription: 'The HR view starts with employee actions, not company-wide sales or media work.',
    metrics: [
      { label: 'Leave requests', value: '4', detail: '2 need review today', icon: CalendarCheck },
      { label: 'Staff updates', value: '5', detail: 'Profiles missing details', icon: Users },
      { label: 'Attendance flags', value: '2', detail: 'Late or missing clock-out', icon: Clock3 },
      { label: 'Admin requests', value: '3', detail: 'Procurement and assets', icon: Archive },
    ],
    primaryPanel: {
      title: 'HR review queue',
      description: 'People actions that need admin attention.',
      items: [
        { title: 'Review Ada annual leave request', meta: 'Starts 1 Jul 2026 - 3 days', status: 'PENDING' },
        { title: 'Complete employee profile cleanup', meta: '5 staff records missing phone or manager', status: 'OPEN' },
        { title: 'Assign new laptop asset', meta: 'WBX-LAP-001 - waiting for owner', status: 'READY' },
      ],
    },
    secondaryPanel: {
      title: 'Staff operations pulse',
      description: 'Daily HR signals.',
      items: [
        { title: 'Today attendance log', meta: '1 present - 2 not clocked in', status: 'LIVE' },
        { title: 'Procurement requests', meta: 'Training projector awaiting review', status: 'SUBMITTED' },
        { title: 'Weekly task progress', meta: 'Review staff delivery for this week', status: 'REVIEW' },
      ],
    },
    quickActions: [
      { label: 'Invite staff', href: '/employees', icon: Users },
      { label: 'Review leave', href: '/leave', icon: CalendarCheck },
      { label: 'Manage assets', href: '/admin', icon: PackageCheck },
    ],
  },
  {
    key: 'eap-officer',
    roleCodes: ['EAP_OFFICER'],
    workspaceTitle: 'EAP Coordination Desk',
    sidebarLabel: 'Confidential care',
    splashTitle: 'EAP desk is ready.',
    splashMessage: 'Protected cases, sessions, and sensitive follow-ups are loading.',
    dashboardTitle: 'EAP Coordination Desk',
    dashboardDescription: 'Confidential coordination view for support cases, practitioner sessions, risk levels, and programme care follow-up.',
    focusLabel: 'Confidential care priority',
    focusTitle: 'Handle sensitive support without noise',
    focusDescription: 'The EAP view surfaces case safety, sessions, and protected details before general operations.',
    metrics: [
      { label: 'Active cases', value: '5', detail: '1 high-risk watch', icon: HeartHandshake },
      { label: 'Sessions due', value: '3', detail: 'Next 7 days', icon: Clock3 },
      { label: 'Follow-ups', value: '4', detail: 'Need coordinator action', icon: Headphones },
      { label: 'Programme care', value: '2', detail: 'Wellbeing cohorts open', icon: BriefcaseBusiness },
    ],
    primaryPanel: {
      title: 'Case coordination queue',
      description: 'Protected records that need attention.',
      items: [
        { title: 'EAP-DEMO-001 follow-up', meta: 'Demo Case - next follow-up today', status: 'ACTIVE' },
        { title: 'Schedule practitioner session', meta: 'Coordinator unassigned', status: 'SCHEDULE' },
        { title: 'Review sensitive case note', meta: 'Requires eap.sensitive permission', status: 'PROTECTED' },
      ],
    },
    secondaryPanel: {
      title: 'Care utilization',
      description: 'EAP operational signals.',
      items: [
        { title: 'Cases by risk', meta: '4 low - 1 high', status: 'MONITOR' },
        { title: 'Session completion', meta: '2 completed - 1 no-show', status: 'REVIEW' },
        { title: 'Wellbeing programme prospects', meta: '12 suitable contacts', status: 'OPEN' },
      ],
    },
    quickActions: [
      { label: 'Open EAP cases', href: '/eap', icon: HeartHandshake },
      { label: 'Programme care', href: '/programmes', icon: BriefcaseBusiness },
      { label: 'Sensitive docs', href: '/documents', icon: FileText },
    ],
  },
  {
    key: 'receptionist',
    roleCodes: ['RECEPTIONIST'],
    workspaceTitle: 'Reception & Client Desk',
    sidebarLabel: 'Front desk flow',
    splashTitle: 'Front desk is open.',
    splashMessage: 'Today’s enquiries, callbacks, registrations, and programme support queue are loading.',
    dashboardTitle: 'Reception & Client Desk',
    dashboardDescription: 'Front-desk workspace for enquiries, client follow-ups, registrations, and day-to-day programme support.',
    focusLabel: 'Front desk priority',
    focusTitle: 'Move enquiries into clean follow-up',
    focusDescription: 'Reception needs calls, prospects, appointments, and programme logistics first.',
    metrics: [
      { label: 'New enquiries', value: '8', detail: '3 still unassigned', icon: PhoneCall },
      { label: 'Callbacks due', value: '5', detail: 'Before close of work', icon: Clock3 },
      { label: 'Registrations', value: '6', detail: 'For July cohort', icon: ClipboardList },
      { label: 'Desk tasks', value: '4', detail: 'Assigned to you', icon: CheckSquare2 },
    ],
    primaryPanel: {
      title: 'Front-desk queue',
      description: 'Calls and prospect actions for today.',
      items: [
        { title: 'Call Northstar Consulting', meta: 'Confirm participant list', status: 'DUE' },
        { title: 'Create contact from walk-in enquiry', meta: 'Leadership programme interest', status: 'NEW' },
        { title: 'Send venue confirmation', meta: 'July 2026 cohort', status: 'READY' },
      ],
    },
    secondaryPanel: {
      title: 'Programme support',
      description: 'Operational details reception can move.',
      items: [
        { title: 'Registration list cleanup', meta: '1 duplicate contact to merge', status: 'CHECK' },
        { title: 'Prospect follow-up schedule', meta: '5 calls planned', status: 'TODAY' },
        { title: 'Delivery materials request', meta: 'Waiting for media handoff', status: 'WAITING' },
      ],
    },
    quickActions: [
      { label: 'Open CRM', href: '/crm', icon: Building2 },
      { label: 'Programme list', href: '/programmes', icon: BriefcaseBusiness },
      { label: 'My tasks', href: '/tasks', icon: CheckSquare2 },
    ],
  },
  {
    key: 'it-officer',
    roleCodes: ['IT_OFFICER'],
    workspaceTitle: 'IT Support Workspace',
    sidebarLabel: 'Systems support',
    splashTitle: 'IT workspace online.',
    splashMessage: 'Technical tasks, system support, and assigned assets are loading.',
    dashboardTitle: 'IT Support Workspace',
    dashboardDescription: 'Technical support view for assigned tasks, systems checks, assets, and staff support requests.',
    focusLabel: 'Systems priority',
    focusTitle: 'Resolve technical blockers without exposing sensitive business data',
    focusDescription: 'IT should get technical work and assets, not HR/EAP confidential records.',
    metrics: [
      { label: 'Support tickets', value: '6', detail: '2 high priority', icon: MonitorCog },
      { label: 'Assigned assets', value: '11', detail: '3 need audit', icon: Laptop },
      { label: 'System checks', value: '4', detail: 'All green so far', icon: CheckCircle2 },
      { label: 'Staff requests', value: '3', detail: 'Waiting response', icon: Headphones },
    ],
    primaryPanel: {
      title: 'Technical work queue',
      description: 'Your operational blockers.',
      items: [
        { title: 'Prepare projector for training room', meta: 'Needed before July cohort', status: 'HIGH' },
        { title: 'Audit assigned demo laptop', meta: 'WBX-LAP-001', status: 'OPEN' },
        { title: 'Check login issue for HR user', meta: 'No sensitive data access required', status: 'SUPPORT' },
      ],
    },
    secondaryPanel: {
      title: 'Systems pulse',
      description: 'Technical signals for the day.',
      items: [
        { title: 'Frontend runtime config', meta: 'Healthy in demo mode', status: 'OK' },
        { title: 'Cloud Run readiness', meta: 'Needs credentials before proof', status: 'BLOCKED' },
        { title: 'Supabase auth setup', meta: 'Pending live project values', status: 'PENDING' },
      ],
    },
    quickActions: [
      { label: 'My tasks', href: '/tasks', icon: CheckSquare2 },
      { label: 'Documents', href: '/documents', icon: FileText },
      { label: 'Admin tools', href: '/admin', icon: Archive },
    ],
  },
  {
    key: 'programme-coordinator',
    roleCodes: ['PROGRAMME_COORDINATOR'],
    workspaceTitle: 'Programme Delivery Workspace',
    sidebarLabel: 'Cohort delivery',
    splashTitle: 'Programme desk is ready.',
    splashMessage: 'Cohorts, participants, follow-ups, and delivery tasks are loading.',
    dashboardTitle: 'Programme Delivery Workspace',
    dashboardDescription: 'Coordinator view for cohorts, participant pipelines, follow-ups, delivery readiness, and campaign actions.',
    focusLabel: 'Programme priority',
    focusTitle: 'Fill cohorts and protect delivery quality',
    focusDescription: 'The programme coordinator starts with editions, prospects, participant status, and delivery tasks.',
    metrics: [
      { label: 'Open editions', value: '3', detail: '1 starts soon', icon: BriefcaseBusiness },
      { label: 'Prospects', value: '24', detail: '8 warm leads', icon: Users },
      { label: 'Follow-ups', value: '9', detail: 'Due this week', icon: PhoneCall },
      { label: 'Delivery tasks', value: '5', detail: 'Materials and logistics', icon: ClipboardList },
    ],
    primaryPanel: {
      title: 'Cohort pipeline',
      description: 'Programme movement that needs action.',
      items: [
        { title: 'July 2026 Cohort', meta: '18 prospects - 6 registered', status: 'OPEN' },
        { title: 'Re-engage past no-shows', meta: 'AI prospect plan available', status: 'READY' },
        { title: 'Confirm delivery venue', meta: 'Hybrid session logistics', status: 'WAITING' },
      ],
    },
    secondaryPanel: {
      title: 'Participant actions',
      description: 'Next best actions for conversion.',
      items: [
        { title: 'Move interested contacts to registered', meta: '3 contacts need payment check', status: 'FOLLOW_UP' },
        { title: 'Generate campaign segment', meta: 'Leadership cohort audience', status: 'AI_READY' },
        { title: 'Update attendance history', meta: 'January demo edition', status: 'OPEN' },
      ],
    },
    quickActions: [
      { label: 'Open programmes', href: '/programmes', icon: BriefcaseBusiness },
      { label: 'CRM follow-ups', href: '/crm', icon: Building2 },
      { label: 'AI suggestions', href: '/ai', icon: Sparkles },
    ],
  },
  {
    key: 'content-media',
    roleCodes: ['CONTENT_MEDIA'],
    workspaceTitle: 'Content & Media Studio',
    sidebarLabel: 'Production queue',
    splashTitle: 'Studio queue loading.',
    splashMessage: 'Your media deliverables, programme assets, and production tasks are loading.',
    dashboardTitle: 'Content & Media Studio',
    dashboardDescription: 'Media workspace for creative deliverables, event coverage, programme assets, and weekly production reporting.',
    focusLabel: 'Production priority',
    focusTitle: 'Create the assets that help programmes move',
    focusDescription: 'Media sees production work first, not company-wide staff metrics or client pipelines.',
    metrics: [
      { label: 'Media tasks', value: '5', detail: '2 due this week', icon: Camera },
      { label: 'Programme assets', value: '3', detail: 'Needed for July cohort', icon: FileText },
      { label: 'Content briefs', value: '4', detail: 'Awaiting approval', icon: Megaphone },
      { label: 'Weekly outputs', value: '6', detail: 'Posts, reels, and edits', icon: ClipboardList },
    ],
    primaryPanel: {
      title: 'Production queue',
      description: 'Creative work assigned to the media desk.',
      items: [
        { title: 'Design July cohort announcement', meta: 'Programme asset - due Friday', status: 'IN_PROGRESS' },
        { title: 'Edit testimonial clip', meta: 'Leadership training campaign', status: 'REVIEW' },
        { title: 'Prepare event photo checklist', meta: 'Hybrid cohort coverage', status: 'TODO' },
      ],
    },
    secondaryPanel: {
      title: 'Asset handoffs',
      description: 'Where media connects to operations.',
      items: [
        { title: 'Reception needs delivery flyer', meta: 'For prospect WhatsApp follow-up', status: 'DUE' },
        { title: 'Programme team needs slide cover', meta: 'Emotional Intelligence at Work', status: 'OPEN' },
        { title: 'Upload approved media pack', meta: 'Documents - Programme module', status: 'READY' },
      ],
    },
    quickActions: [
      { label: 'My tasks', href: '/tasks', icon: CheckSquare2 },
      { label: 'Programmes', href: '/programmes', icon: BriefcaseBusiness },
      { label: 'Upload assets', href: '/documents', icon: FileText },
    ],
  },
  {
    key: 'employee',
    roleCodes: ['EMPLOYEE'],
    workspaceTitle: 'Employee Workspace',
    sidebarLabel: 'My work',
    splashTitle: 'Welcome to your workspace.',
    splashMessage: 'Your weekly tasks, leave, documents, and attendance are loading.',
    dashboardTitle: 'Employee Workspace',
    dashboardDescription: 'Personal workspace for weekly task planning, leave requests, documents, and attendance.',
    focusLabel: 'Your work today',
    focusTitle: 'Stay clear on what is assigned to you',
    focusDescription: 'Employees land on personal work, not management or department-wide dashboards.',
    metrics: [
      { label: 'My tasks', value: '4', detail: '1 due today', icon: CheckSquare2 },
      { label: 'This week', value: '4', detail: '1 due today', icon: ClipboardList },
      { label: 'Leave status', value: '1', detail: 'Pending request', icon: CalendarCheck },
      { label: 'Documents', value: '2', detail: 'Personal uploads', icon: FileText },
    ],
    primaryPanel: {
      title: 'My task queue',
      description: 'Assigned work and follow-up items.',
      items: [
        { title: 'Update assigned task status', meta: 'Due today', status: 'TODO' },
        { title: 'Update weekly task progress', meta: 'Keep task status current', status: 'IN_PROGRESS' },
        { title: 'Upload support document', meta: 'Personal document folder', status: 'OPEN' },
      ],
    },
    secondaryPanel: {
      title: 'My admin actions',
      description: 'Personal workflow reminders.',
      items: [
        { title: 'Clock in for today', meta: 'Attendance not recorded yet', status: 'PENDING' },
        { title: 'Leave request', meta: 'Annual leave waiting review', status: 'PENDING' },
        { title: 'Procurement request', meta: 'No open requests', status: 'CLEAR' },
      ],
    },
    quickActions: [
      { label: 'My tasks', href: '/tasks', icon: CheckSquare2 },
      { label: 'Weekly tasks', href: '/tasks', icon: ClipboardList },
      { label: 'Leave', href: '/leave', icon: CalendarCheck },
    ],
  },
]

const fallbackExperience = roleExperiences.find((role) => role.key === 'employee') ?? roleExperiences[0]

export function getRoleExperience(access: AccessUser | null): RoleExperience {
  if (!access) return fallbackExperience
  if (isCeo(access)) return roleExperiences.find((experience) => experience.key === 'ceo') ?? fallbackExperience
  if (access.isSuperAdmin) return roleExperiences.find((experience) => experience.key === 'general-manager') ?? fallbackExperience
  const roleCodes = new Set(access.roles.map((role) => role.code))
  return roleExperiences.find((experience) => experience.roleCodes.some((code) => roleCodes.has(code))) ?? fallbackExperience
}
